/**
 * QC charts — Match tab: what is happening in the game right now.
 *   Partnerships · Fall of wickets · Chase / projection · Phase comparison · Momentum
 */
import { useContext, useMemo, useState } from "react";
import {
  QcFormatContext, formatOvers, inOrder, niceWicket, oversText, phaseBlocks, useInnings, pct0, useBpo } from "./qcData";
import { Card, Empty, Tile, SvgX } from "./qcUi";
import { useTip } from "./useTip";

// Group the ordered balls into innings, each carrying its team colour
// from useInnings so every Match chart paints a side the same way.
function useOrderedInnings(balls) {
  const meta = useInnings(balls);
  return useMemo(() => {
    const byInn = new Map(meta.map((m) => [m.innings, { ...m, balls: [] }]));
    for (const b of inOrder(balls)) byInn.get(b.innings)?.balls.push(b);
    return [...byInn.values()];
  }, [balls, meta]);
}

// ── 1. Partnerships ──────────────────────────────────────────────────
function partnershipsOf(inn) {
  const out = [];
  let cur = null;
  for (const b of inn.balls) {
    const pair = [b.strikerId, b.nonStrikerId].filter(Boolean).sort().join("|");
    if (!cur || cur.pair !== pair) {
      if (cur) out.push(cur);
      cur = { pair, runs: 0, legal: 0, extras: 0, by: new Map(), ended: null, startScore: 0 };
      cur.startScore = out.reduce((t, p) => t + p.runs, 0);
    }
    cur.runs += b.total;
    cur.extras += b.extras;
    if (b.legal) cur.legal += 1;
    const add = (id, name) => {
      if (!id) return;
      const r = cur.by.get(id) || { id, name, runs: 0, balls: 0 };
      if (!r.name && name) r.name = name;
      cur.by.set(id, r);
      return r;
    };
    const s = add(b.strikerId, b.striker);
    add(b.nonStrikerId, b.nonStriker);
    if (s) {
      s.runs += b.offBat;
      if (b.faced) s.balls += 1;
    }
    if (b.wicket) cur.ended = b;
  }
  if (cur) out.push(cur);
  return out.map((p, i) => ({ ...p, n: i + 1, batters: [...p.by.values()] }));
}

const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

function Partnerships({ innings }) {
  const tip = useTip();
  const rows = innings.map((inn) => ({ inn, stands: partnershipsOf(inn) }));
  const max = Math.max(1, ...rows.flatMap((r) => r.stands.map((s) => s.runs)));
  if (!rows.some((r) => r.stands.length)) return <Card title="Partnerships"><Empty>No balls in this selection.</Empty></Card>;
  return (
    <Card title="Partnerships" subtitle="Each stand's runs split between the two batters (extras in grey) · the current stand is outlined">
      <div data-tip-root className="relative space-y-4">
        {rows.map(({ inn, stands }) => (
          <div key={inn.innings}>
            <div className="text-[11px] text-text-secondary mb-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-sm mr-1.5 align-middle" style={{ background: inn.color }} />
              Innings {inn.innings} · {inn.team}
            </div>
            <div className="space-y-1.5">
              {stands.map((s, idx) => {
                const [a, b] = s.batters;
                const live = idx === stands.length - 1 && !s.ended;
                const w = (v) => `${(v / max) * 100}%`;
                return (
                  <div key={s.n} className="grid grid-cols-[52px_1fr_92px] items-center gap-2 text-[11px]"
                    onMouseMove={(ev) => tip.show(ev, [
                      `${ordinal(s.n)} wicket stand · ${s.runs} (${s.legal} balls)`,
                      ...s.batters.map((x) => `${x.name || "?"}: ${x.runs} (${x.balls})`),
                      `Extras ${s.extras}${s.ended ? ` · ended ${s.ended.label}, ${s.ended.dismissed || s.ended.striker} ${niceWicket(s.ended.wicket)}` : " · not out"}`,
                    ])}
                    onMouseLeave={tip.hide}>
                    <span className="text-text-secondary">{ordinal(s.n)}</span>
                    <div className="min-w-0">
                      <div className={`flex h-4 rounded-[3px] overflow-hidden ${live ? "ring-1 ring-emerald-400/80 ring-offset-1 ring-offset-bg-secondary" : ""}`}
                        style={{ width: w(s.runs), minWidth: 4, gap: 2 }}>
                        {a && a.runs > 0 && <div style={{ flexGrow: a.runs, background: inn.color }} />}
                        {b && b.runs > 0 && <div style={{ flexGrow: b.runs, background: inn.color, opacity: 0.55 }} />}
                        {s.extras > 0 && <div style={{ flexGrow: s.extras, background: "var(--qc-axis)" }} />}
                      </div>
                      <div className="text-[10px] text-text-secondary truncate mt-0.5">
                        {a ? `${a.name} ${a.runs}(${a.balls})` : ""}{b ? ` · ${b.name} ${b.runs}(${b.balls})` : ""}
                      </div>
                    </div>
                    <span className="text-right tabular-nums">
                      <span className="text-text-primary font-semibold">{s.runs}</span>
                      <span className="text-text-secondary"> ({s.legal}b){live ? " *" : ""}</span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {tip.node}
      </div>
    </Card>
  );
}

// ── 2. Fall of wickets ───────────────────────────────────────────────
function FallOfWickets({ innings }) {
  const bpo = useBpo();
  const fmt = useContext(QcFormatContext);
  const tip = useTip();
  const data = innings.map((inn) => {
    let runs = 0, legal = 0, n = 0;
    const falls = [];
    for (const b of inn.balls) {
      runs += b.total;
      if (b.legal) legal += 1;
      if (b.wicket) {
        n += 1;
        falls.push({ n, runs, legal, b });
      }
    }
    return { inn, falls, total: runs, legal };
  });
  if (!data.some((d) => d.falls.length)) {
    return <Card title="Fall of wickets"><Empty>No wickets in this selection yet.</Empty></Card>;
  }
  const overs = formatOvers(Math.max(1, ...data.map((d) => Math.ceil(d.legal / bpo))), fmt);
  const W = 360, H = 44, padL = 8, padR = 8;
  const x = (legal) => padL + (W - padL - padR) * (legal / (overs * bpo));
  return (
    <Card title="Fall of wickets" subtitle="Score and over at each wicket · strip runs across the innings">
      <div data-tip-root className="relative space-y-4">
        {data.map(({ inn, falls, total, legal }) => (
          <div key={inn.innings}>
            <div className="text-[11px] text-text-secondary mb-1">
              <span className="inline-block w-2.5 h-2.5 rounded-sm mr-1.5 align-middle" style={{ background: inn.color }} />
              Innings {inn.innings} · {inn.team} <span className="text-text-primary font-semibold tabular-nums">{total}/{falls.length}</span>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto block" role="img" aria-label={`Fall of wickets, innings ${inn.innings}`}>
              <line x1={padL} x2={W - padR} y1={26} y2={26} stroke="var(--qc-grid)" strokeWidth="3" strokeLinecap="round" />
              <line x1={padL} x2={x(legal)} y1={26} y2={26} stroke={inn.color} strokeWidth="3" strokeLinecap="round" />
              {falls.map((f, i) => (
                <g key={f.n} onMouseMove={(ev) => tip.show(ev, [
                  `${f.runs}/${f.n} · over ${oversText(f.legal, bpo)}`,
                  `${f.b.dismissed || f.b.striker} — ${niceWicket(f.b.wicket)}`,
                  `Bowler ${f.b.bowler}${f.b.caughtBy ? ` · c ${f.b.caughtBy}` : ""}`,
                ])} onMouseLeave={tip.hide}>
                  <text x={x(f.legal)} y={i % 2 ? 42 : 13} fontSize="7" textAnchor="middle" className="fill-text-primary font-semibold" style={{ fontVariantNumeric: "tabular-nums" }}>{`${f.runs}/${f.n}`}</text>
                  <circle cx={x(f.legal)} cy={26} r="7" fill="transparent" />
                  <g transform={`translate(${x(f.legal)},26)`}>
                    <circle r="4.6" fill="rgba(15,23,42,0.9)" stroke={inn.color} strokeWidth="1.2" />
                    <path d="M-2,-2 l4,4 M2,-2 l-4,4" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" />
                  </g>
                </g>
              ))}
            </svg>
            <table className="w-full text-[11px] mt-1">
              <tbody className="tabular-nums">
                {falls.map((f) => (
                  <tr key={f.n} className="border-t border-border-default">
                    <td className="py-1 w-14 text-text-primary font-semibold">{f.runs}/{f.n}</td>
                    <td className="w-12 text-text-secondary">{oversText(f.legal, bpo)}</td>
                    <td className="text-text-primary truncate">{f.b.dismissed || f.b.striker}</td>
                    <td className="text-text-secondary truncate">
                      {niceWicket(f.b.wicket)}{f.b.caughtBy ? ` · c ${f.b.caughtBy}` : ""}{f.b.stumpedBy ? ` · st ${f.b.stumpedBy}` : ""}{f.b.runOutBy ? ` · ${f.b.runOutBy}` : ""} · b {f.b.bowler}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
        {tip.node}
      </div>
    </Card>
  );
}

// ── 3. Chase / projection ────────────────────────────────────────────
function Chase({ innings }) {
  const bpo = useBpo();
  const fmt = useContext(QcFormatContext);
  const [hover, setHover] = useState(null);
  const first = innings[0];
  const second = innings[1];
  const overs = formatOvers(Math.max(1, ...innings.map((i) => Math.ceil(i.balls.filter((b) => b.legal).length / bpo))), fmt);
  if (!first) return <Card title="Chase"><Empty>No balls in this selection.</Empty></Card>;

  // First innings only: where is this heading?
  if (!second) {
    const legal = first.balls.filter((b) => b.legal).length;
    const runs = first.balls.reduce((t, b) => t + b.total, 0);
    const left = Math.max(0, overs * bpo - legal) / bpo;
    const rr = legal ? (runs * bpo) / legal : 0;
    const proj = (rate) => Math.round(runs + rate * left);
    return (
      <Card title="Projected score" subtitle={`Innings 1 · ${first.team} · ${runs} after ${oversText(legal, bpo)} of ${overs} overs · the chase view appears when innings 2 starts`}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Tile label="At current RR" value={proj(rr)} sub={`${rr.toFixed(2)} per over`} />
          {[8, 10, 12].map((r) => <Tile key={r} label={`At ${r} an over`} value={proj(r)} sub={`${left.toFixed(1)} overs left`} />)}
        </div>
      </Card>
    );
  }

  const target = first.balls.reduce((t, b) => t + b.total, 0) + 1;
  const pts = [];
  let runs = 0, legal = 0, wk = 0;
  for (const b of second.balls) {
    runs += b.total;
    if (b.wicket) wk += 1;
    if (b.legal) {
      legal += 1;
      if (legal % bpo === 0 || b === second.balls[second.balls.length - 1]) {
        const left = overs * bpo - legal;
        pts.push({ over: legal / bpo, rr: (runs * bpo) / legal, req: left > 0 ? Math.max(0, ((target - runs) * bpo) / left) : null, runs, wk });
      }
    }
  }
  const need = Math.max(0, target - runs);
  const ballsLeft = Math.max(0, overs * bpo - legal);
  const reqNow = ballsLeft ? (need * bpo) / ballsLeft : null;
  const rrNow = legal ? (runs * bpo) / legal : 0;
  const done = need === 0 ? `${second.team} won` : wk >= 10 || ballsLeft === 0 ? `${first.team} won` : null;

  const W = 360, H = 170, padL = 26, padB = 18, padT = 12, padR = 8;
  const maxY = Math.max(12, ...pts.flatMap((p) => [p.rr, p.req ?? 0]).map((v) => Math.min(v, 24)));
  const x = (o) => padL + (W - padL - padR) * (o / overs);
  const y = (v) => padT + (H - padT - padB) * (1 - Math.min(v, maxY) / maxY);
  const line = (key) => pts.filter((p) => p[key] != null).map((p) => `${x(p.over)},${y(p[key])}`).join(" ");
  const ticks = [];
  for (let t = 0; t <= maxY; t += maxY > 16 ? 4 : 2) ticks.push(t);

  return (
    <Card title="Chase — run rate vs required rate" subtitle={`Target ${target} in ${overs} overs · ${second.team} chasing ${first.team}`}>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        <Tile label="Score" value={`${runs}/${wk}`} sub={`${oversText(legal, bpo)} overs`} />
        <Tile label="Need" value={done ? "—" : need} sub={done || `from ${ballsLeft} balls`} />
        <Tile label="Required RR" value={reqNow != null && !done ? reqNow.toFixed(2) : "—"} tone={reqNow != null && reqNow > rrNow + 2 && !done ? "warn" : undefined} sub="per over" />
        <Tile label="Current RR" value={rrNow.toFixed(2)} sub="per over" />
      </div>
      <div className="relative">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto block" role="img" aria-label="Run rate against required rate"
          onMouseMove={(ev) => {
            const r = ev.currentTarget.getBoundingClientRect();
            const o = Math.round((((ev.clientX - r.left) / r.width) * W - padL) / (W - padL - padR) * overs);
            setHover(pts.find((p) => Math.round(p.over) === o) || null);
          }}
          onMouseLeave={() => setHover(null)}>
          {ticks.map((t) => (
            <g key={t}>
              <line x1={padL} x2={W - padR} y1={y(t)} y2={y(t)} stroke="var(--qc-grid)" strokeWidth="0.6" />
              <text x={padL - 4} y={y(t)} fontSize="7" textAnchor="end" dominantBaseline="central" className="fill-text-secondary">{t}</text>
            </g>
          ))}
          {Array.from({ length: Math.floor(overs / 5) + 1 }, (_, i) => i * 5).map((o) => (
            <text key={o} x={x(o)} y={H - 5} fontSize="7" textAnchor="middle" className="fill-text-secondary">{o}</text>
          ))}
          <polyline points={line("req")} fill="none" stroke="var(--qc-axis)" strokeWidth="2" strokeDasharray="5 3" strokeLinejoin="round" />
          <polyline points={line("rr")} fill="none" stroke={second.color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
          {hover && <line x1={x(hover.over)} x2={x(hover.over)} y1={padT} y2={H - padB} stroke="var(--qc-axis)" strokeWidth="0.8" />}
        </svg>
        {hover && (
          <div className="pointer-events-none absolute top-1 left-10 rounded-md border border-border-default bg-bg-primary/95 px-2 py-1.5 text-[11px] shadow-lg">
            <div className="font-semibold text-text-primary">After {hover.over.toFixed(1).replace(/\.0$/, "")} overs · {hover.runs}/{hover.wk}</div>
            <div className="text-text-secondary">Run rate <span className="text-text-primary tabular-nums">{hover.rr.toFixed(2)}</span></div>
            {hover.req != null && <div className="text-text-secondary">Required <span className="text-text-primary tabular-nums">{hover.req.toFixed(2)}</span></div>}
          </div>
        )}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 text-[10px] text-text-secondary">
        <span className="inline-flex items-center gap-1.5"><svg width="18" height="6" aria-hidden><line x1="1" y1="3" x2="17" y2="3" stroke={second.color} strokeWidth="2" /></svg>Run rate ({second.team})</span>
        <span className="inline-flex items-center gap-1.5"><svg width="18" height="6" aria-hidden><line x1="1" y1="3" x2="17" y2="3" stroke="var(--qc-axis)" strokeWidth="2" strokeDasharray="4 2" /></svg>Required rate</span>
      </div>
    </Card>
  );
}

// ── 4. Phase comparison ──────────────────────────────────────────────
function PhaseComparison({ innings }) {
  const bpo = useBpo();
  const fmt = useContext(QcFormatContext);
  const overs = formatOvers(Math.max(1, ...innings.map((i) => Math.ceil(i.balls.filter((b) => b.legal).length / bpo))), fmt);
  const blocks = phaseBlocks(overs, fmt);
  const rows = innings.map((inn) => ({
    inn,
    ph: blocks.map((bk) => {
      const bs = inn.balls.filter((b) => b.over >= bk.from && b.over <= bk.to);
      const legal = bs.filter((b) => b.legal).length;
      return {
        ...bk,
        runs: bs.reduce((t, b) => t + b.total, 0),
        wkts: bs.filter((b) => b.wicket).length,
        legal,
        dots: bs.filter((b) => b.legal && b.total === 0).length,
        fours: bs.filter((b) => b.offBat === 4).length,
        sixes: bs.filter((b) => b.offBat === 6).length,
      };
    }),
  }));
  if (!rows.length) return <Card title="Phase comparison"><Empty>No balls in this selection.</Empty></Card>;
  const maxRr = Math.max(6, ...rows.flatMap((r) => r.ph.map((p) => (p.legal ? (p.runs * bpo) / p.legal : 0))));
  return (
    <Card title="Phase comparison" subtitle="Each innings' run rate per phase, same scale · table has the detail">
      <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${blocks.length}, minmax(0,1fr))` }}>
        {blocks.map((bk, bi) => (
          <div key={bk.key} className="rounded-lg border border-border-default p-2">
            <div className="text-[10px] uppercase tracking-wider text-text-secondary mb-2">{bk.label} <span className="normal-case">({bk.from}–{bk.to})</span></div>
            <div className="flex items-end gap-2 h-24">
              {rows.map(({ inn, ph }) => {
                const p = ph[bi];
                const rr = p.legal ? (p.runs * bpo) / p.legal : 0;
                return (
                  <div key={inn.innings} className="flex-1 flex flex-col items-center justify-end h-full min-w-0">
                    <span className="text-[10px] text-text-primary font-semibold tabular-nums">{p.legal ? rr.toFixed(1) : "—"}</span>
                    <div className="w-full rounded-t-[4px]" style={{ height: `${(rr / maxRr) * 80}%`, minHeight: p.legal ? 2 : 0, background: inn.color }} />
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2 mt-1">
              {rows.map(({ inn, ph }) => (
                <div key={inn.innings} className="flex-1 text-center text-[10px] text-text-secondary tabular-nums truncate">{ph[bi].legal ? `${ph[bi].runs}/${ph[bi].wkts}` : ""}</div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <table className="w-full text-[11px] mt-3">
        <thead className="text-text-secondary uppercase tracking-wider text-[9px]">
          <tr><th className="text-left py-1 font-normal">Innings</th><th className="text-left font-normal">Phase</th><th className="text-right font-normal">Runs/W</th><th className="text-right font-normal">Ov</th><th className="text-right font-normal">RR</th><th className="text-right font-normal">Dot %</th><th className="text-right font-normal">4s</th><th className="text-right font-normal">6s</th></tr>
        </thead>
        <tbody className="tabular-nums">
          {rows.flatMap(({ inn, ph }) => ph.filter((p) => p.legal).map((p) => (
            <tr key={`${inn.innings}${p.key}`} className="border-t border-border-default">
              <td className="py-1 text-text-primary"><span className="inline-block w-2 h-2 rounded-sm mr-1.5" style={{ background: inn.color }} />{inn.team}</td>
              <td className="text-text-secondary">{p.label}</td>
              <td className="text-right text-text-primary font-semibold">{p.runs}/{p.wkts}</td>
              <td className="text-right text-text-secondary">{oversText(p.legal, bpo)}</td>
              <td className="text-right text-text-primary">{((p.runs * bpo) / p.legal).toFixed(2)}</td>
              <td className="text-right text-text-secondary">{pct0(p.dots, p.legal)}</td>
              <td className="text-right text-text-secondary">{p.fours}</td>
              <td className="text-right text-text-secondary">{p.sixes}</td>
            </tr>
          )))}
        </tbody>
      </table>
    </Card>
  );
}

// ── 5. Momentum ──────────────────────────────────────────────────────
const WINDOW = 12;

function Momentum({ innings }) {
  const bpo = useBpo();
  const tip = useTip();
  const series = innings.map((inn) => {
    const legal = inn.balls.filter((b) => b.legal || b.wicket);
    const pts = [];
    let q = [];
    let dotRun = 0, bestDots = 0, sinceBoundary = 0;
    const streaks = [];
    inn.balls.forEach((b) => {
      if (b.offBat === 4 || b.offBat === 6) sinceBoundary = 0;
      else if (b.legal) sinceBoundary += 1;
      if (!b.legal) {
        if (q.length) q[q.length - 1].total += b.total;
        return;
      }
      q.push({ total: b.total });
      if (q.length > WINDOW) q = q.slice(-WINDOW);
      const idx = pts.length + 1;
      if (b.total === 0) {
        dotRun += 1;
        if (dotRun === 1) streaks.push({ from: idx, to: idx });
        else streaks[streaks.length - 1].to = idx;
      } else dotRun = 0;
      bestDots = Math.max(bestDots, dotRun);
      pts.push({ idx, rr: (q.reduce((t, x) => t + x.total, 0) * bpo) / q.length, wicket: !!b.wicket, b });
    });
    return { inn, pts, streaks: streaks.filter((s) => s.to - s.from + 1 >= 4), dotRun, bestDots, sinceBoundary, n: legal.length };
  });
  if (!series.some((s) => s.pts.length)) return <Card title="Momentum"><Empty>No balls in this selection.</Empty></Card>;
  const live = series[series.length - 1];
  const maxBalls = Math.max(24, ...series.map((s) => s.pts.length));
  const maxY = Math.max(12, ...series.flatMap((s) => s.pts.map((p) => p.rr)));
  const W = 360, H = 150, padL = 24, padB = 16, padT = 10, padR = 6;
  const x = (i) => padL + (W - padL - padR) * (i / maxBalls);
  const y = (v) => padT + (H - padT - padB) * (1 - v / maxY);
  const ticks = [];
  for (let t = 0; t <= maxY; t += maxY > 24 ? 12 : 6) ticks.push(t);
  return (
    <Card wide title="Momentum" subtitle={`Rolling run rate over the last ${WINDOW} legal balls · grey bands = 4+ dot balls in a row · ✕ = wicket`}>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        <Tile label="Dot streak now" value={live.dotRun} sub={`${live.inn.team}, innings ${live.inn.innings}`} tone={live.dotRun >= 6 ? "warn" : undefined} />
        <Tile label="Balls since a boundary" value={live.sinceBoundary} sub={live.sinceBoundary >= 18 ? "pressure building" : "legal balls"} tone={live.sinceBoundary >= 18 ? "warn" : undefined} />
        <Tile label="Longest dot streak" value={live.bestDots} sub="this innings" />
        <Tile label={`RR, last ${WINDOW} balls`} value={live.pts.length ? live.pts[live.pts.length - 1].rr.toFixed(2) : "—"} sub="per over" />
      </div>
      <div data-tip-root className="relative grid grid-cols-1 lg:grid-cols-2 gap-4">
        {series.map((s) => (
          <div key={s.inn.innings}>
            <div className="text-[11px] text-text-secondary mb-1">
              <span className="inline-block w-2.5 h-2.5 rounded-sm mr-1.5 align-middle" style={{ background: s.inn.color }} />
              Innings {s.inn.innings} · {s.inn.team}
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto block" role="img" aria-label={`Momentum, innings ${s.inn.innings}`}>
              {s.streaks.map((st) => (
                <rect key={st.from} x={x(st.from - 1)} y={padT} width={x(st.to) - x(st.from - 1)} height={H - padT - padB} fill="var(--qc-axis)" opacity="0.18" />
              ))}
              {ticks.map((t) => (
                <g key={t}>
                  <line x1={padL} x2={W - padR} y1={y(t)} y2={y(t)} stroke="var(--qc-grid)" strokeWidth="0.6" />
                  <text x={padL - 4} y={y(t)} fontSize="7" textAnchor="end" dominantBaseline="central" className="fill-text-secondary">{t}</text>
                </g>
              ))}
              {Array.from({ length: Math.floor(maxBalls / 30) + 1 }, (_, i) => i * 30).map((i) => (
                <text key={i} x={x(i)} y={H - 4} fontSize="7" textAnchor="middle" className="fill-text-secondary">{i / bpo}</text>
              ))}
              <polyline points={s.pts.map((p) => `${x(p.idx)},${y(p.rr)}`).join(" ")} fill="none" stroke={s.inn.color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
              {s.pts.filter((p) => p.wicket).map((p) => (
                <g key={p.idx} onMouseMove={(ev) => tip.show(ev, [`${p.b.label} · wicket`, `${p.b.dismissed || p.b.striker} ${niceWicket(p.b.wicket)} · b ${p.b.bowler}`])} onMouseLeave={tip.hide}>
                  <circle cx={x(p.idx)} cy={y(p.rr)} r="6" fill="transparent" />
                  <SvgX x={x(p.idx)} y={y(p.rr)} s={3} />
                </g>
              ))}
            </svg>
          </div>
        ))}
        {tip.node}
      </div>
      <p className="text-[10px] text-text-secondary mt-1">x-axis = overs (legal balls ÷ {bpo})</p>
    </Card>
  );
}

export default function MatchCharts({ balls }) {
  const innings = useOrderedInnings(balls);
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <Chase innings={innings} />
      <PhaseComparison innings={innings} />
      <Partnerships innings={innings} />
      <FallOfWickets innings={innings} />
      <Momentum innings={innings} />
    </div>
  );
}
